import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '@/hooks/useApiMutation';
import { apiPost, operationsClient } from '@/lib/api/client';
import { taxZoneEndpoints } from '@/lib/api/endpoints';
import { taxZoneKeys, taxGroupKeys } from '@/lib/queryKeys';
import type {
  CreateTaxZoneFromPolygonRequest,
  CreateTaxZoneFromPolygonResponse,
  DroppedAuthorityEntry,
} from '@/types/tax-polygon';

/**
 * NOT-95 step 5 — TanStack Query mutation for the polygon-to-TaxZone writeback.
 *
 * On success:
 *   1. Invalidate the tax-zone + tax-group caches so the newly-created
 *      zone shows up in the list view without a manual refresh.
 *   2. Emit a success toast with the created/reused TaxCode counts.
 *   3. If the BE dropped any admin picks (stale-rate / rate-changed) or
 *      surfaced failed ZIPs, follow with warning toasts so the admin sees
 *      the drift instead of assuming a clean write.
 *
 * Errors fall through to the default `useApiMutation` error toast (no
 * suppression: a failed writeback is a real failure the admin must see).
 */
export function useCreateTaxZoneFromPolygon() {
  const qc = useQueryClient();

  return useApiMutation<CreateTaxZoneFromPolygonResponse, CreateTaxZoneFromPolygonRequest>({
    mutationFn: (data) =>
      apiPost<CreateTaxZoneFromPolygonResponse, CreateTaxZoneFromPolygonRequest>(
        operationsClient,
        taxZoneEndpoints.createFromPolygon(),
        data,
      ),
    onSuccess: (response) => {
      void qc.invalidateQueries({ queryKey: taxZoneKeys.all() });
      void qc.invalidateQueries({ queryKey: taxGroupKeys.all() });

      const createdCount = response.created.taxCodeIds.length;
      const reusedCount = response.reused.taxCodeIds.length;
      toast.success(
        `Tax zone created — ${createdCount} code${createdCount === 1 ? '' : 's'} new, ${reusedCount} reused`,
      );

      if (response.dropped.length > 0) {
        toast.warning(formatDroppedWarning(response.dropped));
      }
      if (response.failedZips.length > 0) {
        toast.warning(formatFailedZipsWarning(response.failedZips));
      }
    },
  });
}

/**
 * Human label for each drop reason. Kept exported so the test can pin the
 * exact toast wording without duplicating the table.
 */
const DROPPED_REASON_LABEL: Record<DroppedAuthorityEntry['reason'], string> = {
  'stale-rate': 'stale rate',
  'rate-changed': 'rate changed',
};

function formatDroppedWarning(dropped: DroppedAuthorityEntry[]): string {
  // Group by reason so the toast stays scannable even with many drops.
  const counts = new Map<DroppedAuthorityEntry['reason'], number>();
  for (const drop of dropped) {
    counts.set(drop.reason, (counts.get(drop.reason) ?? 0) + 1);
  }
  const parts = Array.from(counts.entries()).map(
    ([reason, count]) => `${count} ${DROPPED_REASON_LABEL[reason]}`,
  );
  return `${dropped.length} authority pick${
    dropped.length === 1 ? '' : 's'
  } dropped (${parts.join(', ')})`;
}

function formatFailedZipsWarning(failedZips: string[]): string {
  const preview = failedZips.slice(0, 5).join(', ');
  const suffix = failedZips.length > 5 ? `, +${failedZips.length - 5} more` : '';
  return `${failedZips.length} ZIP${
    failedZips.length === 1 ? '' : 's'
  } failed to resolve: ${preview}${suffix}`;
}
